import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';

import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import OptionsButtonComponent from 'webapp/components/options-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type ConnectRequest from 'webapp/models-custom/connect-request';

interface Args {
	model: ConnectRequest;
}

type Action = 'resendConnectRequestMail' | 'deleteRequestConnect';
export type Option = PopupListItem<Action>;

export default class DataManagerConnectedTableRowRequestedComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;

	@tracked showAll = false;
	@tracked submitInProgress = false;

	get model(): ConnectRequest {
		return this.args.model;
	}

	get options(): Option[] {
		const options: Option[] = [];

		options.push({
			icon: 'Mail',
			text: 'Resend email',
			action: 'resendConnectRequestMail',
		});

		options.push({
			class: 'delete',
			text: 'Delete request',
			action: 'deleteRequestConnect',
			icon: 'Trash',
		});

		return options;
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	resendConnectRequestMail(): void {
		if (this.submitInProgress) {
			return;
		}

		const layer = this.infolayerService.loading('Resending email...');
		this.submitInProgress = true;
		this.requestService
			.api('POST', `/connect/by-request/resend/${this.model.flashToken}`)
			.then(
				() => {
					layer.ok('Email successfully resent!');
					this.submitInProgress = false;
				},
				() => {
					layer.close();
					this.submitInProgress = false;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	deleteRequestConnect(): void {
		const sourceType = this.model?.sourceType,
			title =
				'Are you sure you want to revoke this request' +
				(sourceType ? ` to connect ${sourceType.name}` : '') +
				'?',
			onConfirm = () => {
				const layer = this.infolayerService.loading('Deleting request for connect...');
				this.requestService
					.api('DELETE', `/connect/by-request/${this.model.flashToken}`)
					.then(
						() => {
							layer.ok('Request for connect successfully removed!');
						},
						() => {
							layer.close();
						},
					)
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			};

		this.popupService.openConfirmPopup({
			onConfirm,
			title,
			text: '',
			confirmAction: 'Revoke',
			cancelAction: 'Cancel',
		});
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}
	<template>
		<tr class="table-row pending-source filter-all {{if this.showAll 'all-shown'}}">
			<td class="c0 no-grow first">
				<img src={{this.model.sourceType.avatar}} alt />
			</td>
			<td class="c1">
				<strong>
					{{this.model.sourceType.name}}
				</strong>
				<span>
					Pending request
					<ToolTipComponent @text={{this.model.requestSentText}} />
				</span>
			</td>
			<td class="requested prio-low" data-label="Requested: ">
				{{{this.model.requestedOnText}}}
			</td>
			<td class="perm prio-low" data-label="Permissions: ">
				N/A
			</td>
			<td class="prio-medium" data-label="Sync: ">
				N/A
			</td>
			<td class="prio-medium" data-label="Status: ">
				N/A
			</td>
			<td class="metrics prio-medium" data-label="Available metrics: ">
				N/A
			</td>
			<td class="c6 button-ct last">
				<OptionsButtonComponent
					@options={{this.options}}
					@onAction={{this.doOptionsAction}}
				/>
			</td>

			<FlexibleToggleCellComponent
				@showAll={{this.showAll}}
				@toggleShowAll={{this.toggleShowAll}}
			/>

		</tr>
	</template>
}
